import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { Permission } from '../models/Permission'
import { permissionSchema } from '../models/schema'
import {
  createPermission as createPermissionAPI,
  deletePermission as deletePermissionAPI,
  fetchPermissionById,
  fetchPermissions,
  updatePermission as updatePermissionAPI,
} from '../services/PermissionService'
import { AppDispatch, RootState } from './store'

const initialState: SBState<Permission> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'permission',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getPermissionsSuccess,
  setQuery: setPermissionQuery,
  reset: resetPermissionState,
  resetQueryAndIds: resetPermissionQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: Permission }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectPermissions = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedPermission = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectPermissionById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectPermissionsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedPermission =
  (permission: Permission | number) => async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(getIdOrModelId<Permission>(permission))
    )

export const getPermissions = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchPermissions(params),
    [permissionSchema],
    slice.actions
  )

export const createPermission = (permission: Permission) =>
  SBAPI.CreateDispatched(
    createPermissionAPI(permission),
    permissionSchema,
    slice.actions
  )

export const getPermissionWithId = (id: number) =>
  SBAPI.FetchDispatched<Permission>(
    fetchPermissionById(id),
    permissionSchema,
    slice.actions
  )

export const updatePermission = (permission: Permission) =>
  SBAPI.UpdateDispatched<Permission>(
    updatePermissionAPI(permission),
    permission,
    slice.actions
  )

export const deletePermission = (permission: Permission) =>
  SBAPI.DestroyDispatched<Permission>(
    deletePermissionAPI(permission),
    permission,
    slice.actions
  )
